/**
 * Sprint 1G — Action confirmation page.
 *
 * Security posture:
 *  - Sets `Referrer-Policy: no-referrer` via <meta> so the token isn't leaked
 *    on outbound clicks.
 *  - Never stores the token in localStorage / sessionStorage / cookies.
 *  - Removes the token from the visible URL via history.replaceState() after
 *    capturing it in memory. Any later refresh loads a bare URL.
 *  - GET never mutates state; only an explicit button click POSTs.
 *  - Expired / revoked / already-used / test-mode tokens display a calm
 *    status page rather than an error.
 *
 * Action type routing (Sprint 1G · footer actions fully wired):
 *  - MANAGE_PREFERENCES → Preferences panel (Pause / Resume / Unsubscribe / Get help)
 *  - GET_HELP           → Help panel (mailto: support)
 *  - PAUSE_BRIEF        → Confirm to pause → success screen with Resume button
 *  - UNSUBSCRIBE        → Confirm to unsubscribe → success screen
 *  - OWNER_WILL_HANDLE  → Confirm to mark handled by owner
 *  - REQUEST_GROWTH_PARTNER_HELP → Confirm to open a service request
 */

import { useEffect, useRef, useState } from "react";
import {
  inspectActionTokenV2,
  applyActionTokenV2,
  applyPreferenceActionV2,
} from "@/lib/api";
import {
  Loader2, CheckCircle2, ShieldCheck, AlertCircle, LifeBuoy, Settings, Pause, Play,
} from "lucide-react";

const ACTION_LABEL = {
  OWNER_WILL_HANDLE:           "Mark this recommendation as handled by the owner",
  REQUEST_GROWTH_PARTNER_HELP: "Request help from your Growth Partner",
  PAUSE_BRIEF:                 "Pause Monday Brief",
  UNSUBSCRIBE:                 "Unsubscribe from Monday Brief",
  MANAGE_PREFERENCES:          "Manage email preferences",
  GET_HELP:                    "Need help with your Monday Brief?",
};

const ACTION_ICON = {
  OWNER_WILL_HANDLE:           CheckCircle2,
  REQUEST_GROWTH_PARTNER_HELP: LifeBuoy,
  PAUSE_BRIEF:                 ShieldCheck,
  UNSUBSCRIBE:                 ShieldCheck,
  MANAGE_PREFERENCES:          Settings,
  GET_HELP:                    LifeBuoy,
};

export default function ActionConfirm() {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);
  // Guard against React 18 StrictMode's dev-only double-invoke of effects —
  // without this the second run finds a token-less URL (we stripped it on
  // the first run) and overwrites the good state with 'Missing token.'
  const capturedRef = useRef(false);

  useEffect(() => {
    if (capturedRef.current) return;
    capturedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token") || "";
    if (t) {
      setToken(t);
      try { window.history.replaceState({}, "", window.location.pathname); }
      catch (_) { /* no-op */ }
    } else {
      setError("Missing token.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const d = await inspectActionTokenV2(t);
        if (!d.ok) setError(d.reason || "Token could not be verified.");
        else setMeta(d);
      } catch (e) {
        setError("This link could not be verified. It may have expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "referrer";
    m.content = "no-referrer";
    document.head.appendChild(m);
    return () => {
      try { document.head.removeChild(m); } catch (_) {} // eslint-disable-line
    };
  }, []);

  const onConfirm = async () => {
    if (!token || !meta) return;
    setApplying(true);
    try {
      const d = await applyActionTokenV2(token);
      setApplied(d.outcome || { message: "Done." });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unable to complete this action.";
      setError(String(detail));
    } finally {
      setApplying(false);
    }
  };

  // For the Manage-preferences page — direct preference mutations using
  // the MANAGE_PREFERENCES token.
  const onPreferenceAction = async (action) => {
    if (!token) return;
    setApplying(true);
    try {
      const d = await applyPreferenceActionV2(token, action);
      const out = d.outcome || {};
      // Reflect fresh state locally so buttons re-render immediately.
      if (out.currentPreferences) {
        setMeta((prev) => prev ? { ...prev, currentPreferences: out.currentPreferences } : prev);
      }
      setApplied(out);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unable to complete this action.";
      setError(String(detail));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: "var(--ta-surface, #F9F7F3)" }}
      data-testid="action-confirm-page"
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-sm bg-white p-8"
        style={{ border: "1px solid var(--ta-border, #E5E7EB)" }}
      >
        <PageHeader />

        {loading && (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--ta-text-secondary, #4B5563)" }}>
            <Loader2 size={16} className="animate-spin" />
            Verifying link…
          </div>
        )}

        {!loading && error && (
          <StatusPanel
            icon={AlertCircle}
            tone="warn"
            title="This link can't be used"
            body={error}
            testId="action-confirm-error"
          />
        )}

        {!loading && !error && meta && !applied && (
          <ActionBody
            meta={meta}
            applying={applying}
            onConfirm={onConfirm}
            onPreferenceAction={onPreferenceAction}
          />
        )}

        {!loading && applied && (
          <ActionApplied
            applied={applied}
            meta={meta}
            applying={applying}
            onPreferenceAction={onPreferenceAction}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page pieces
// ---------------------------------------------------------------------------

const PageHeader = () => (
  <div className="mb-6">
    <div
      className="text-[11px] uppercase tracking-widest font-semibold"
      style={{ color: "var(--ta-primary, #1F3A5F)" }}
    >
      Monday Brief
    </div>
    <div className="text-xs mt-1" style={{ color: "var(--ta-text-secondary, #4B5563)" }}>
      Confirming a link from your email
    </div>
  </div>
);

const Meta = ({ meta }) => (
  <div className="mb-5" data-testid="action-confirm-meta">
    <div className="text-lg font-semibold" style={{ color: "var(--ta-primary, #1F3A5F)" }}>
      {ACTION_LABEL[meta.actionType] || meta.actionType}
    </div>
    {meta.businessName && (
      <div className="text-sm mt-1" style={{ color: "var(--ta-text-secondary, #4B5563)" }}>
        For {meta.businessName}
      </div>
    )}
  </div>
);

const StatusPanel = ({ icon: Icon, tone, title, body, testId }) => {
  const bg = tone === "warn" ? "#FBEDE5" : "#E5F0EB";
  const ink = tone === "warn" ? "#7A2E1E" : "#1F4936";
  return (
    <div className="rounded-lg p-4 flex items-start gap-3" style={{ background: bg, color: ink }} data-testid={testId}>
      <Icon size={20} />
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-sm mt-1" style={{ opacity: 0.9 }}>{body}</div>
      </div>
    </div>
  );
};

const TestBanner = () => (
  <div
    className="mt-4 text-xs rounded-lg p-3"
    style={{ background: "#FBEDE5", color: "#7A2E1E" }}
    data-testid="action-confirm-test-banner"
  >
    <strong>Test mode.</strong> This is a preview of the link that would go out in a
    real Monday Brief. Confirming here won&apos;t change anything.
  </div>
);

const ActionButton = ({ testId, onClick, disabled, icon: Icon, children, tone = "primary" }) => {
  const primary = tone === "primary";
  const bg = primary ? "var(--ta-accent, #D96C51)" : "#F3F4F6";
  const fg = primary ? "#FFF" : "#1F2937";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="w-full mt-3 rounded-full px-5 py-3 font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
      style={{ background: bg, color: fg }}
    >
      {disabled ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />}
      {children}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Body per action type
// ---------------------------------------------------------------------------

function ActionBody({ meta, applying, onConfirm, onPreferenceAction }) {
  if (meta.actionType === "MANAGE_PREFERENCES") {
    return <ManagePreferencesBody meta={meta} applying={applying} onPreferenceAction={onPreferenceAction} />;
  }
  if (meta.actionType === "GET_HELP") {
    return <GetHelpBody meta={meta} />;
  }
  // Confirm-required actions (PAUSE_BRIEF, UNSUBSCRIBE, OWNER_WILL_HANDLE,
  // REQUEST_GROWTH_PARTNER_HELP) share the same layout.
  return <ConfirmActionBody meta={meta} applying={applying} onConfirm={onConfirm} />;
}

function ConfirmActionBody({ meta, applying, onConfirm }) {
  const isPause = meta.actionType === "PAUSE_BRIEF";
  const isUnsub = meta.actionType === "UNSUBSCRIBE";
  const businessName = meta.businessName || "your business";
  return (
    <>
      <Meta meta={meta} />

      {isPause && (
        <StatusPanel
          icon={Pause}
          tone="ok"
          title={`Pause Monday Brief for ${businessName}?`}
          body={"Only the explicit confirmation below will pause weekly briefs. Nothing changes when the link is opened."}
          testId="pause-brief-explanation"
        />
      )}

      {isUnsub && (
        <StatusPanel
          icon={ShieldCheck}
          tone="ok"
          title={`Unsubscribe ${businessName} from Monday Brief?`}
          body={"Only the explicit confirmation below will unsubscribe. Nothing changes when the link is opened."}
          testId="unsubscribe-explanation"
        />
      )}

      {meta.usedAt ? (
        <StatusPanel
          icon={CheckCircle2}
          tone="ok"
          title="Already confirmed"
          body="This action was already applied. There's nothing more to do."
          testId="action-confirm-already-used"
        />
      ) : (
        <>
          <ActionButton
            testId="action-confirm-btn"
            onClick={onConfirm}
            disabled={applying}
            icon={CheckCircle2}
            tone="primary"
          >
            {applying
              ? "Applying…"
              : isPause
                ? "Pause Monday Brief"
                : isUnsub
                  ? "Unsubscribe"
                  : `Yes, ${(ACTION_LABEL[meta.actionType] || "").toLowerCase()}`}
          </ActionButton>

          {isPause && (
            <p className="text-xs mt-3 text-center" data-testid="pause-brief-cancel-hint"
               style={{ color: "var(--ta-text-secondary, #4B5563)" }}>
              Or just close this tab to keep receiving Monday Brief.
            </p>
          )}
        </>
      )}

      {meta.testMode && <TestBanner />}
    </>
  );
}

// -- Manage preferences ------------------------------------------------------

function ManagePreferencesBody({ meta, applying, onPreferenceAction }) {
  const prefs = meta.currentPreferences || {};
  const status = prefs.unsubscribed
    ? "unsubscribed"
    : prefs.paused
      ? "paused"
      : "active";
  const statusLabel = status === "active" ? "Active" : status === "paused" ? "Paused" : "Unsubscribed";
  const statusIcon = status === "active" ? Play : status === "paused" ? Pause : ShieldCheck;

  return (
    <div data-testid="manage-preferences-body">
      <Meta meta={meta} />

      <StatusPanel
        icon={AlertCircle}
        tone="ok"
        title="This page never changes settings by itself"
        body={"Nothing here changes any settings on its own. Each action below requires an explicit click."}
        testId="manage-preferences-security-note"
      />

      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-widest font-semibold"
             style={{ color: "var(--ta-primary, #1F3A5F)" }}>
          Current status
        </div>
        <div className="mt-1 flex items-center gap-2" data-testid="preferences-current-status">
          {(() => { const I = statusIcon; return <I size={16} />; })()}
          <span className="text-lg font-semibold" data-testid="preferences-current-status-label">
            {statusLabel}
          </span>
        </div>
        {status === "paused" && prefs.pausedAt && (
          <p className="text-xs mt-1" style={{ color: "var(--ta-text-secondary, #4B5563)" }}>
            Paused since {new Date(prefs.pausedAt).toLocaleDateString()}
          </p>
        )}
      </div>

      {status === "unsubscribed" ? (
        <StatusPanel
          icon={ShieldCheck}
          tone="ok"
          title="You're unsubscribed"
          body={"Sign up again from your dashboard to receive Monday Brief in the future."}
          testId="preferences-unsubscribed-terminal"
        />
      ) : (
        <div className="mt-4 space-y-1">
          {status === "active" && (
            <ActionButton
              testId="preferences-pause-btn"
              onClick={() => onPreferenceAction("PAUSE")}
              disabled={applying}
              icon={Pause}
              tone="secondary"
            >
              Pause Monday Brief
            </ActionButton>
          )}
          {status === "paused" && (
            <ActionButton
              testId="preferences-resume-btn"
              onClick={() => onPreferenceAction("RESUME")}
              disabled={applying}
              icon={Play}
              tone="primary"
            >
              Resume Monday Brief
            </ActionButton>
          )}
          <ActionButton
            testId="preferences-unsubscribe-btn"
            onClick={() => onPreferenceAction("UNSUBSCRIBE")}
            disabled={applying}
            icon={ShieldCheck}
            tone="secondary"
          >
            Unsubscribe
          </ActionButton>
        </div>
      )}

      <div className="mt-6 pt-4 border-t text-sm" style={{ borderColor: "var(--ta-border, #E5E7EB)" }}>
        <a
          href="mailto:briefs@bullsharkbuilds.com?subject=Monday%20Brief%20help"
          className="inline-flex items-center gap-2 hover:underline"
          data-testid="preferences-get-help-link"
          style={{ color: "var(--ta-primary, #1F3A5F)" }}
        >
          <LifeBuoy size={14} /> Get help with your Monday Brief
        </a>
      </div>

      {meta.testMode && <TestBanner />}
    </div>
  );
}

// -- Get help ----------------------------------------------------------------

function GetHelpBody({ meta }) {
  const support = meta.supportEmail || "briefs@bullsharkbuilds.com";
  const subject = encodeURIComponent(
    `Monday Brief help${meta.businessName ? ` for ${meta.businessName}` : ""}`
  );
  return (
    <div data-testid="get-help-body">
      <Meta meta={meta} />
      <StatusPanel
        icon={LifeBuoy}
        tone="ok"
        title="Need help with your Monday Brief?"
        body={"We're a small team and we read every message. Send us a note and we'll come back to you."}
        testId="get-help-explanation"
      />
      <ActionButton
        testId="get-help-mailto-btn"
        onClick={() => { window.location.href = `mailto:${support}?subject=${subject}`; }}
        disabled={false}
        icon={LifeBuoy}
        tone="primary"
      >
        Email {support}
      </ActionButton>
      {meta.testMode && <TestBanner />}
    </div>
  );
}

// -- Post-apply screens ------------------------------------------------------

function ActionApplied({ applied, meta, applying, onPreferenceAction }) {
  const actionType = applied.actionType || meta?.actionType;

  // PAUSE via the confirm flow (PAUSE_BRIEF) — surface a Resume button
  // using the freshly-minted MANAGE_PREFERENCES token.
  if (actionType === "PAUSE_BRIEF" && applied.preferencesToken) {
    return (
      <PauseAppliedWithResume applied={applied} applying={applying} />
    );
  }

  // PAUSE via preferences page (from ManagePreferencesBody).
  if (actionType === "PAUSE") {
    return (
      <div data-testid="preferences-pause-applied">
        <StatusPanel
          icon={CheckCircle2}
          tone="ok"
          title="Monday Brief is paused."
          body="You will not receive weekly briefs until you resume them."
          testId="preferences-pause-success"
        />
        <ActionButton
          testId="preferences-resume-after-pause-btn"
          onClick={() => onPreferenceAction("RESUME")}
          disabled={applying}
          icon={Play}
          tone="primary"
        >
          Resume Monday Brief
        </ActionButton>
      </div>
    );
  }

  if (actionType === "RESUME") {
    return (
      <StatusPanel
        icon={CheckCircle2}
        tone="ok"
        title="Monday Brief has been resumed."
        body="You will start receiving your weekly Monday Brief again."
        testId="preferences-resume-success"
      />
    );
  }

  if (actionType === "UNSUBSCRIBE") {
    return (
      <StatusPanel
        icon={CheckCircle2}
        tone="ok"
        title="You've been unsubscribed."
        body={applied.message || "You will no longer receive Monday Brief emails."}
        testId="preferences-unsubscribe-success"
      />
    );
  }

  return (
    <StatusPanel
      icon={CheckCircle2}
      tone="ok"
      title="Done"
      body={applied.message || "Your request was processed."}
      testId="action-confirm-success"
    />
  );
}

// The PAUSE_BRIEF success screen embeds a Resume button that uses the
// freshly-minted MANAGE_PREFERENCES token returned in the apply outcome.
// Kept inline (rather than mounting a second ActionConfirm) so the user
// never navigates away from the confirmation surface.
function PauseAppliedWithResume({ applied, applying }) {
  const [resumeApplying, setResumeApplying] = useState(false);
  const [resumeState, setResumeState] = useState(null);

  const resume = async () => {
    setResumeApplying(true);
    try {
      const d = await applyPreferenceActionV2(applied.preferencesToken, "RESUME");
      setResumeState(d.outcome || { message: "Monday Brief has been resumed." });
    } catch (e) {
      setResumeState({
        message: e?.response?.data?.detail || e?.message || "Unable to resume right now.",
        applied: false,
      });
    } finally {
      setResumeApplying(false);
    }
  };

  if (resumeState) {
    return (
      <StatusPanel
        icon={CheckCircle2}
        tone="ok"
        title="Monday Brief has been resumed."
        body={resumeState.message || "You will start receiving your weekly Monday Brief again."}
        testId="pause-brief-resume-success"
      />
    );
  }

  return (
    <div data-testid="pause-brief-applied-with-resume">
      <StatusPanel
        icon={CheckCircle2}
        tone="ok"
        title="Monday Brief is paused."
        body="You will not receive weekly briefs until you resume them."
        testId="pause-brief-success"
      />
      <ActionButton
        testId="pause-brief-resume-btn"
        onClick={resume}
        disabled={applying || resumeApplying}
        icon={Play}
        tone="primary"
      >
        Resume Monday Brief
      </ActionButton>
    </div>
  );
}
